import React, { useRef, useState } from "react";
import { faCode, faEllipsisV, faEraser, faExchangeAlt } from "@fortawesome/free-solid-svg-icons";
import { TranslationFieldValue } from "./TranslationFieldValue";
import Editor from "../../LexicalEditor";
import { prettyHtml } from "../../../utils";
import Monaco from '@monaco-editor/react';
import { parseLexicalValueString } from "../../LexicalEditor/plugins/EditorStatePlugin";
// import FormFieldCodeMirror from "../FormFieldCodeMirror";
import { DropdownItem, DropdownMenu, DropdownToggle, FormGroup, UncontrolledDropdown } from "reactstrap";
import { Controlled as CodeMirror } from 'react-codemirror2';
import MediaSelectionModal from "../../../features/mediaLibrary/MediaSelectionModal";

/**
 * @typedef {import('../../LexicalEditor/plugins/ToolbarPlugin/Toolbar').IToolbarActionDefinition} IToolbarActionDefinition
 */

/**
 * @typedef FormFieldTranslationEditorProperties
 * @property {import("react-final-form").FieldRenderProps<any, HTMLElement, any>} properties
 * @property {() => IToolbarActionDefinition[]} additionalButtons
 * @property {string[]} editorVariables
 * @property {any} editorProfile
 * @property {any} customSettings
 * @property {any} customStyles
 * @property {any} charControlHandler
 * @property {any} resetValue
 * @property {(html: string) => void} onChange
 * @property {(value: string) => void} [onPreviewClick]
 */

/**
 * @param {FormFieldTranslationEditorProperties} param0
 */
export default function FormFieldTranslationEditor
(
    {
        properties,
        additionalButtons,
        editorVariables,
        editorProfile,
        customSettings,
        customStyles,
        charControlHandler,
        resetValue,
        onChange,
        onPreviewClick
    }
)
{
    const [sourceCode, setSourceCode] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editorResetVersion, setEditorResetVersion] = useState(0);
    /** @type {React.MutableRefObject<any>} */
    const editorRef = useRef(null);
    const { format, value } = TranslationFieldValue.parse(properties.input.value);
    // if (properties.input.name === 'descriptionDesktop-Pl-translations') { console.log(properties.input.value, format); }
    if (format === 'lexical')
    {
        const handleConvertToHtml = () =>
        {
            // console.log('handleConvertToHtml');
            const { html } = parseLexicalValueString(value);
            const serialized = TranslationFieldValue.serializeHtml(html);
            properties.input.onChange({ target: { value: serialized } });
        };
        const customButtons = () =>
        {
            const buttons = additionalButtons?.() || [];
            const convertToHtmlButton =
            {
                hint: 'Convert to source code',
                text: 'Konwertuj do kodu źródłowego',
                icon: faExchangeAlt,
                type: 'button',
                onSelected: handleConvertToHtml
            };
            // const previewButton =
            // {
            //     hint: 'Preview',
            //     icon: faEye,
            //     type: 'button',
            //     onSelected: () => {}
            // };
            const sourceCodeButton =
            {
                hint: 'Source code',
                icon: faCode,
                type: 'button',
                onSelected: () => { setSourceCode(true); }
            };
            /** @type {IToolbarActionDefinition} */
            const additionalActionsButton =
            {
                hint: 'Additional actions',
                type: '',
                icon: faEllipsisV,
                group: 'end',
                loadChildren: callback => { callback([convertToHtmlButton]); }
            };
            // buttons.push(previewButton);
            buttons.push(sourceCodeButton);
            buttons.push(additionalActionsButton);
            if (resetValue)
            {
                buttons.push({
                    hint: 'Przywróć wartość domyślną',
                    icon: faEraser,
                    type: 'button',
                    group: 'end',
                    onSelected: () => {
                        const shouldReplace = window.confirm('Zastąpić aktualną treść wartością domyślną?');
                        if (!shouldReplace) { return; }
                        properties.input.onChange(resetValue);
                        setEditorResetVersion(version => version + 1);
                    }
                });
            }
            return buttons;
        };
        if (sourceCode)
        {
            const isError = (properties.meta.error || properties.meta.submitError) && properties.meta.touched;
            const value = typeof properties.input.value === 'string' && properties.input.value.startsWith('lexical:')
                ? properties.input.value.substring(8)
                : properties.input.value || '';
            const element =
            <>
                <FormGroup>
                    <CodeMirror
                        name={properties.input.name}
                        value={value}
                        onBeforeChange={(_editor, _data, value) => {
                            properties.input.onChange({ target: { value: 'lexical:' + value } });
                        }}
                        options={{ mode: 'htmlmixed', theme: 'material', lineNumbers: true }}
                    />
                    <button type="button" className="btn btn-sm btn-secondary mt-2" onClick={() => setSourceCode(false)}>
                        <i className="fa fa-eye mr-2" />
                        <span>Powrót do edytora</span>
                    </button>
                </FormGroup>
                {isError && <p className='text-warning'>{properties.meta.error || properties.meta.submitError}</p>}
            </>;
            return element;
        }
        else
        {
            const element =
            <Editor
                key={`${properties.input.name}:${editorResetVersion}`}
                {...properties.input}
                generateCustomButtons={customButtons}
                variables={editorVariables}
                customSettings={customSettings}
                customStyles={customStyles}
                charControlHandler={charControlHandler}
                profile={editorProfile}
            />;
            return element;
        }
    }
    else
    {
        const htmlValue = value; // RichText.toHtml(value);
        const handleInsertVariable = variable =>
        {
            const editor = editorRef.current;
            if (!editor) { return; }
            const selection = editor.getSelection();
            const edit = { range: selection, text: `{${variable}}`, forceMoveMarkers: true };
            editor.executeEdits('insert-variable', [ edit ]);
            editor.focus();
        };
        const handleConvertToLexical = () =>
        {
            const serialized = TranslationFieldValue.serializeHtmlAsLexical(htmlValue);
            properties.input.onChange({ target: { value: serialized } });
        };
        const handleFormat = () =>
        {
            properties.input.onChange({ target: { value: prettyHtml(htmlValue) } });
        };
        const handleOnChange = (/** @type {string|undefined} */ newValue) =>
        {
            properties.input.onChange({ target: { value: newValue } });
            onChange(newValue || '');
        };
        const addImageFromMediaLibrary = (/** @type {import("../../../features/mediaLibrary/MediaSelectionModal").MediaItem[]} */ files) =>
        {
            const editor = editorRef.current;
            if (!editor) { return; }
            const imagesHtml = files.map(file => `<img src="${file.path}" />`).join('');
            // const model = editor.getModel();
            const selection = editor.getSelection();
            editor.executeEdits('insert-image', [{ range: selection, text: imagesHtml, forceMoveMarkers: true }]);
            setIsModalOpen(false);
        };
        const element =
        <>
            <div className="p-1 d-flex border-bottom">
                <button type="button" className="btn btn-sm btn-secondary mr-1" onClick={handleFormat}>
                    <i className="fa fa-magic mr-2" />
                    <span>Formatuj</span>
                </button>
                <button type="button" className="btn btn-sm btn-secondary mr-1">
                    <i className="fa fa-eraser mr-2" />
                    <span>Wyczyść</span>
                </button>
                <button type="button" className="btn btn-sm btn-secondary mr-1" onClick={() => setIsModalOpen(true)}>
                    <i className="fa fa-image mr-2" />
                    <span>Wstaw obraz</span>
                </button>
                <UncontrolledDropdown size="sm" className="mr-1">
                    <DropdownToggle
                        caret
                        color="secondary"
                        size="sm"
                        disabled={!editorVariables?.length}
                    >
                        <i className="fa fa-tag mr-2" />
                        <span>Wstaw zmienną</span>
                    </DropdownToggle>
                    <DropdownMenu>
                        {editorVariables?.map(variable => (
                            <DropdownItem
                                key={variable}
                                onClick={() => handleInsertVariable(variable)}
                            >
                                <code>{variable}</code>
                            </DropdownItem>
                        ))}
                    </DropdownMenu>
                </UncontrolledDropdown>
                {onPreviewClick &&
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => onPreviewClick(htmlValue)}>
                        <i className="fa fa-eye mr-2" />
                        <span>Podgląd</span>
                    </button>}
                <div className="flex-grow-1"></div>
                <button type="button" className="btn btn-sm btn-secondary" onClick={handleConvertToLexical}>
                    <i className="fa fa-exchange-alt mr-2" />
                    <span>Konwertuj do edytora</span>
                </button>
            </div>
            <Monaco
                height="50vh"
                language="html"
                value={htmlValue}
                onChange={handleOnChange}
                onMount={(editor) => { editorRef.current = editor; }}
            />
            {isModalOpen && <MediaSelectionModal defaultItemsChecked={[]} isOpen={isModalOpen} setIsOpen={setIsModalOpen} onSubmit={addImageFromMediaLibrary} />}
        </>;
        return element;
    }
}
